import React from 'react'
import { connect } from 'react-redux'

import { StyledFeatureTogglePage } from 'component-library/feature-toggling/ExperimentalFeatures/ExperimentalFeatures.styled'
import { ToggleDescription } from 'common/feature-togglings/features'
import { TogglesState, overrideFeatureEnabled } from 'common/feature-togglings/reducer'
import { FeatureFlag } from 'component-library/feature-toggling/ExperimentalFeatures/components/FeatureFlag'
import { groupBy } from 'common/helpers/utils'
import { AppState } from 'common/store'
import { Features } from 'common/features'

interface MappedProps {
    toggles: TogglesState
}

interface DispatchProps {
    overrideEnabled: typeof overrideFeatureEnabled
}

type UserToggleableFeature = {
    feature: Features
    group: string
    description: string
    enabled: boolean
    overrideValue: boolean | undefined
}

export interface Props {
    featureDescriptions: { [key in Features]: ToggleDescription }
}

type InternalProps = Props & MappedProps & DispatchProps

export function createProductUserOverrides() {
    const RawUserOverride: React.FC<InternalProps> = ({
        featureDescriptions,
        toggles,
        overrideEnabled,
    }) => {
        const features = Object.keys(featureDescriptions) as Features[]
        const userToggleableFlags = features.reduce<Array<UserToggleableFeature>>(
            (userToggleable, feature) => {
                const toggle = toggles.featureState[feature]

                if (toggle && toggles.clientOverridable[feature]) {
                    userToggleable.push({
                        feature,
                        group: featureDescriptions[feature].group,
                        description: featureDescriptions[feature].displayName,
                        enabled: toggle.enabled,
                        overrideValue: toggles.userOverrides[feature],
                    })
                }

                return userToggleable
            },
            []
        )

        const grouped = groupBy(userToggleableFlags, value => value.group).sort(
            (a, b) => (a.key > b.key ? 1 : a.key < b.key ? -1 : 0)
        )
        const featureFlags = grouped.map(({ key, values }) => {
            return (
                <div key={key}>
                    <h2>{key}</h2>
                    {values
                        .sort(
                            (a, b) =>
                                a.description > b.description
                                    ? 1
                                    : a.description < b.description
                                        ? -1
                                        : 0
                        )
                        .map(({ feature, description, enabled, overrideValue }) => (
                            <FeatureFlag
                                key={feature}
                                title={description}
                                defaultValue={enabled}
                                userOverride={overrideValue}
                                onToggle={() =>
                                    overrideEnabled(
                                        feature,
                                        overrideValue === undefined ? !enabled : !overrideValue
                                    )
                                }
                            />
                        ))}
                </div>
            )
        })
        return <StyledFeatureTogglePage>{featureFlags}</StyledFeatureTogglePage>
    }

    const mapStateToProps = ({ toggles }: { toggles: TogglesState }) => ({ toggles })
    const mapDispatchToProps = { overrideEnabled: overrideFeatureEnabled }

    const UserOverride = connect<MappedProps, DispatchProps, Props, AppState>(
        mapStateToProps,
        mapDispatchToProps
    )(RawUserOverride)

    return UserOverride
}
